import { Alert, Breadcrumbs, Button, Chip, Grid, Paper, Typography } from "@mui/material";
import * as React from "react";

import { Helpers } from "../../utils/helpers";
import { TournamentSourceMeta } from "./api";

interface TournamentPageHeaderProps {
    title: string;
    description?: string;
    breadcrumbs?: Array<{
        label: string;
        path?: string;
    }>;
    onNavigate?: (path: string) => void;
    action?: React.ReactNode;
}

interface TournamentSectionProps {
    title: string;
    action?: React.ReactNode;
    children: React.ReactNode;
}

interface TournamentKeyValueGridProps {
    items: Array<{
        label: string;
        value: React.ReactNode;
    }>;
}

interface TournamentJsonBlockProps {
    title: string;
    data: unknown;
}

interface TournamentEmptyStateProps {
    title: string;
    description: string;
}

export const renderTournamentValue = (value: unknown, fallback = "Not set") => {
    if (value === null || value === undefined || value === "") {
        return fallback;
    }

    if (typeof value === "boolean") {
        return value ? "Yes" : "No";
    }

    return String(value);
};

export const formatTournamentDuration = (seconds?: number | null) => {
    if (!seconds || seconds <= 0) {
        return "n/a";
    }

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes}m ${Helpers.padLeadingZero(remainingSeconds)}s`;
};

export const renderTournamentStatus = (status?: string | null) => {
    const normalized = String(status || "unknown").toLowerCase();
    const styles = {
        finished: { backgroundColor: "#2e7d32", color: "#fff" },
        running: { backgroundColor: "#ed6c02", color: "#fff" },
        pending: { backgroundColor: "#5c6bc0", color: "#fff" },
        active: { backgroundColor: "#2e7d32", color: "#fff" },
        review: { backgroundColor: "#ed6c02", color: "#fff" },
        draft: { backgroundColor: "#546e7a", color: "#fff" },
    };

    return <Chip
        size="small"
        label={renderTournamentValue(status, "unknown")}
        style={styles[normalized] || { backgroundColor: "#757575", color: "#fff" }}
    />;
};

export const TournamentPageHeader = ({
    title,
    description,
    breadcrumbs = [],
    onNavigate,
    action,
}: TournamentPageHeaderProps) => <Paper className="root" elevation={0}>
    <div style={{ float: "left", padding: "15px 22px 15px 22px", width: "100%" }}>
        <div style={{ float: "left" }}>
            {breadcrumbs.length > 0 && <Breadcrumbs aria-label="breadcrumb" style={{ marginBottom: 8 }}>
                {breadcrumbs.map((item, index) => {
                    const isLast = index === breadcrumbs.length - 1;

                    if (!item.path || isLast || !onNavigate) {
                        return <Typography color={isLast ? "textPrimary" : "textSecondary"} key={`${item.label}-${index}`}>
                            {item.label}
                        </Typography>;
                    }

                    return <Button
                        key={`${item.label}-${index}`}
                        size="small"
                        color="inherit"
                        onClick={() => onNavigate(item.path || "/")}
                        style={{ minWidth: 0, padding: 0 }}
                    >
                        {item.label}
                    </Button>;
                })}
            </Breadcrumbs>}

            <Typography variant="h6">{title}</Typography>

            {description && <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {description}
            </Typography>}
        </div>

        {action && <div style={{ float: "right" }}>{action}</div>}
    </div>
</Paper>;

export const TournamentSourceNotice = ({ meta }: { meta?: TournamentSourceMeta | null }) => {
    if (!meta) {
        return null;
    }
    return <></>
    // return <Alert severity="info" style={{ marginBottom: 16 }}>
    //     {meta.sourceStatus === "demo_dataset"
    //         ? "This Tournament explorer is currently powered by a repo-local adapter dataset until the real tournament source is wired."
    //         : "This Tournament explorer is using a non-authoritative adapter source."}
    // </Alert>;
};

export const TournamentSection = ({ title, action, children }: TournamentSectionProps) => <Paper variant="outlined" style={{ padding: 16, marginTop: 16 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Typography variant="subtitle1">{title}</Typography>
        {action}
    </div>
    {children}
</Paper>;

export const TournamentKeyValueGrid = ({ items }: TournamentKeyValueGridProps) => <Grid container spacing={2}>
    {items.map(item => <Grid item xs={12} md={6} key={item.label}>
        <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
            {item.label}
        </Typography>
        <Typography variant="body1">
            {item.value}
        </Typography>
    </Grid>)}
</Grid>;

export const TournamentJsonBlock = ({ title, data }: TournamentJsonBlockProps) => <TournamentSection title={title}>
    <Typography
        component="pre"
        variant="body2"
        style={{
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "monospace",
        }}
    >
        {JSON.stringify(data, null, 2)}
    </Typography>
</TournamentSection>;

export const TournamentEmptyState = ({ title, description }: TournamentEmptyStateProps) => <Paper variant="outlined" style={{ padding: 24, textAlign: "center" }}>
    <Typography variant="subtitle1">{title}</Typography>
    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
        {description}
    </Typography>
</Paper>;
